<template>
  <div class="product-details">
    <input id="product-name" type="hidden" :value="product.name" />
    <input id="product-code" type="hidden" :value="variant.code" />
    <input id="product-brand" type="hidden" :value="variant.brandNameFull" />
    <section
      v-if="!purchaseMode || product.isArchived"
      id="buying-zone"
      class="b2b-buying-zone grid-container pt-4"
      data-pdp
    >
      <pdp-product-header :product="product" />
      <div class="b2b-buying-zone__content grid-x grid-margin-x mt-3 md:mt-5 mb-2">
        <div class="cell medium-12 large-8">
          <div
            class="b2b-buying-zone__carousel-container buying-zone__carousel mb-3 md:mb-5"
            :data-brand-name="product.brandNameFull"
          >
            <!-- Product Image Panel -->
            <pdp-product-image-panel
              v-if="hasImages"
              :product="product"
              :color="selectedColor"
              :max-thumbnails="options.maxPdpThumbnails"
            />
            <div v-else>
              <img
                src="https://media.trekbikes.com/image/upload/w_800,h_600,f_auto,q_auto/default-no-image"
                :alt="$t('skuGrid.B2B.noImage')"
              />
            </div>
          </div>
        </div>
        <div class="cell medium-12 large-4">
          <div class="b2b-buying-zone__info-options-container grid-x grid-margin-x">
            <div class="b2b-buying-zone__info cell medium-6 large-12">
              <div data-product-details>
                <!-- Product Price -->
                <span class="font-medium text-md flex-grow-0">
                  <product-price
                    v-if="variantCode"
                    :variant-code="variantCode"
                    :is-archived="product.isArchived"
                    is-trek-u-checkout-enabled="isTrekUCheckoutEnabled"
                  />
                </span>
                <product-reviews-summary class="mt-2 buying-zone" :product-power-reviews-id="product.powerReviewsID" />
                <product-summary :product="product" class="border-b-transparent" />
              </div>
              <div
                v-if="copyPositioningStatement.length > 0"
                class="buying-zone__positioning-statement mb-2 pt-2 border-t border-gray-10"
              >
                <p qaid="product-positioning-statement" v-html="copyPositioningStatement" />
              </div>
            </div>

            <footer class="b2b-buying-zone__options cell medium-6 large-12 lg:pt-2">
              <!-- Attributes Selection -->
              <product-attributes-container
                ref="attributesContainer"
                v-model="variantCode"
                :product="product"
                :init-attribute-selection="initAttributeSelection"
                @color-changed="
                  (color) => {
                    selectedColor = color;
                  }
                "
              />
              <!-- Display SKU of Selected Variant -->
              <div
                v-if="variantCode && variantCode.length > 0"
                class="pdp-sku-number flex justify-between my-2 items-center"
              >
                <div />
                <div qaid="buying-zone.sku" class="flex">
                  <span class="buying-zone__code text-gray-60" v-html="buyingZoneCode"></span>
                  <trek-link
                    v-show="options.hasUserAccessToBuyingZone"
                    button
                    text
                    icon-only
                    icon="history"
                    :href="orderHistoryUrl"
                    class="ml-1"
                    qaid="buying-zone.orderHistory"
                    aria-label="Order history"
                  >
                  </trek-link>
                </div>
              </div>

              <div v-if="product.isArchived">
                <div v-if="product.archivedProductCurrentCategoryLink" class="mt-2">
                  <trek-link button primary block :href="product.archivedProductCurrentCategoryLink">
                    <span>{{ $t('productArchive.seeCurrent') }}</span>
                  </trek-link>
                </div>
                <pdp-replacement v-if="variantCode" :replacement-product="replacementProduct" />
              </div>
              <div v-else>
                <pdl-callout v-if="variant.replacementProduct">
                  <template #content>
                    <p qaid="active-product-replacement-heading" class="mb-1">{{ $t('productArchive.replacement') }}</p>
                    <pdl-link qaid="active-product-replacement-link" standalone :href="replacementProductLink">
                      {{ $t('productArchive.viewCurrent') }}
                    </pdl-link>
                  </template>
                </pdl-callout>
                <!-- Display Availability -->
                <pdp-product-warehouses
                  v-show="!isSpectrumEnabled && options.hasUserAccessToBuyingZone"
                  v-model="product.code"
                  :product-type="product.productType"
                  @availability-change="onAvailabilityChange"
                />
                <!-- Add to Cart -->
                <customize-product-button
                  :is-buyable="hasAnyPrices"
                  :project-one-url="projectOneUrl"
                  :spectrum-url="product.spectrumUrl"
                  :custom-configurator-type="product.customConfiguratorType"
                  :is-project-one-configurator-active="options.isProjectOneConfiguratorActive"
                  :custom-product-starting-point="product.customProductStartingPoint"
                  :site-locale="options.encodedContextPath"
                  :is-b2b="true"
                  :options="options"
                />
                <add-to-cart-container
                  v-show="!isSpectrumEnabled"
                  :options="options"
                  :product="product"
                  :variant-code="variantCode"
                  :variant-max-order-quantity="variant.maxOrderQuantity"
                  :is-adding-to-cart="isAddingToCart"
                  :is-purchasable="hasPrice && hasAvailability"
                  :min-quantity="minQuantity"
                  :has-availability="hasAvailability"
                  :add-to-cart-button-caption="addToCartButtonCaption"
                  :success-message="cartSuccessMessage"
                  :has-user-access-to-buying-zone="options.hasUserAccessToBuyingZone"
                  @add-to-cart="beforeAddToCart"
                />
              </div>
              <pdp-rpa-results
                v-if="canShowRpa && options.rpaFunctionalityActive"
                :is-bike="isBike"
                :retailer-data="retailerData"
                :is-location-postal-code-correct="isLocationPostalCodeCorrect"
                :options="options"
                :product="product"
              />
            </footer>
          </div>
        </div>
      </div>
    </section>

    <bundling-dialog
      :show-dialog="isBundleDialogVisible"
      :add-to-cart-button-caption="addToCartButtonCaption"
      @error="addToCartNow(variantCode, quantity)"
      @cancel="setBundleDialogVisibility({type: 'BUYING', isVisible: false})"
      @add-to-cart="setBundleDialogVisibility({type: 'BUYING', isVisible: false})"
    />
  </div>
</template>
<script>
import {mapActions, mapState, mapGetters, mapMutations} from 'vuex';
import {InitProductAttributes} from '@/components/containers/pdp/utils/init-product-attributes';
import get from 'lodash/get';
import {AddToCart} from '@/components/containers/pdp/utils/add-to-cart';
import PdpProductHeader from '@/components/containers/pdp/b2b/PdpProductHeader';
import ProductReviewsSummary from '@/components/power-reviews/ProductReviewsSummary';
import ProductSummary from '@/components/containers/buy-zone/ProductSummary.vue';
import ProductPrice from '@/components/containers/buy-zone/ProductPrice';
import ProductAttributesContainer from '@/components/containers/buy-zone/b2b/ProductAttributesContainer.vue';
import PdpProductWarehouses from '@/components/containers/pdp/b2b/PdpProductWarehouses';
import AddToCartContainer from '@/components/containers/buy-zone/b2b/AddToCartContainer.vue';
import PdpProductImagePanel from '@/components/containers/pdp/PdpProductImagePanel';
import {ProductDetails} from '@/components/containers/pdp/utils/product-details.js';
import {loadVariantPricing} from '@/components/containers/pdp/utils/product-pricing.js';
import PdpRpaResults from '@/components/containers/pdp/PdpRpaResults';
import PdpReplacement from '@/components/containers/pdp/PdpReplacement';
import TrekLink from '@/components/TrekLink';
import {PdlCallout} from '@pedal/pdl-callout';
import CustomizeProductButton from '@/components/containers/buy-zone/CustomizeProductButton';
import BundlingDialog from '@/components/containers/pdp/BundlingDialog';
import Variant from '@/utils/variant-helpers';
import {PdlLink} from '@pedal/pdl-link';
import {Url} from '@/utils/url';
import {useFunctionalityToggle} from '@/composables/functionality-toggle.js';
import {FunctionalityToggle} from '@/constants/functionality-toggle.js';

export default {
  name: 'ProductDetails',
  components: {
    PdpReplacement,
    PdpRpaResults,
    PdpProductImagePanel,
    AddToCartContainer,
    PdpProductWarehouses,
    ProductAttributesContainer,
    ProductPrice,
    ProductSummary,
    ProductReviewsSummary,
    PdpProductHeader,
    TrekLink,
    PdlCallout,
    CustomizeProductButton,
    BundlingDialog,
    PdlLink,
  },
  mixins: [AddToCart, InitProductAttributes, ProductDetails],
  setup() {
    const {isToggleActive: isTrekUCheckoutEnabled} = useFunctionalityToggle(
      FunctionalityToggle.TREKU_EMPLOYEE_PURCHASE_ENABLED
    );

    return {isTrekUCheckoutEnabled};
  },
  data() {
    return {
      hasAvailability: false,
      quantity: 0,
    };
  },
  computed: {
    ...mapState('backend', ['encodedContextPath', 'isConsumerFriendlyMode']),
    ...mapState('productDetails', ['pdpDetailsLoaded', 'isBuyingZoneBundleDialogVisible']),
    ...mapState('viewSize', ['viewBreakpoint']),
    ...mapGetters('storage', ['purchaseMode']),
    ...mapGetters('productDetails', ['hasPrice']),

    hasProductSummary() {
      return !!this.product?.summary;
    },
    orderHistoryUrl() {
      return `${this.encodedContextPath}/my-account/orders?partNumber=${this.variantCode}`;
    },
    minQuantity() {
      return get(this.pricingData, 'vendorMinQty', get(this.product, 'vendorMinQty', 0));
    },
    hasAnyPrices() {
      return this.product?.variants.some(
        (variant) =>
          variant.prices?.retailerPrice?.price?.formatted || variant.prices?.retailerPrice?.price?.low?.formatted
      );
    },
    replacementProduct() {
      if (!this.variantCode) {
        return null;
      }

      return get(
        this.product.variants.find((variant) => variant.code === this.variantCode),
        'replacementProduct'
      );
    },
    replacementProductLink() {
      const url = get(this.replacementProduct, 'url');
      return !url ? '' : Url.withContextPath(url);
    },

    canShowRpa() {
      if (!this.product) {
        return false;
      }

      const allowedByOptions = !get(this.options, 'kioskModeEnabledOnSite');
      const hasRetailers = get(this.retailerData, 'retailers', []).length > 0;
      const isVariantSelected = this.variantCode && this.variantCode.length > 0;

      return allowedByOptions && hasRetailers && isVariantSelected && !this.isConsumerFriendlyMode;
    },
    buyingZoneCode() {
      return `${this.$t('product.variants.sku')} ${this.variantCode}`;
    },
    isSpectrumEnabled() {
      return !!this.product.spectrumUrl;
    },

    isBundleDialogVisible: {
      get() {
        return this.isBuyingZoneBundleDialogVisible;
      },

      set(isVisible) {
        this.setBundleDialogVisibility({type: 'BUYING', isVisible});
      },
    },
  },
  watch: {
    variantCode(code) {
      const productCode = code ? code : this.product.code;
      this.setVariantCode(productCode);

      const mechanicNotes = Variant(productCode, this.product).get()?.mechanicsNotes;
      this.setMechanicNotes(mechanicNotes);

      loadVariantPricing(productCode);
      // set this to true if variant code changes
      this.setIsVariantsChanged(true);
    },
  },
  mounted() {
    // Set base product's ID for B2B section only.
    this.setVariantCode(this.product.code);
    window.addEventListener('load', this.pdpFullyLoaded());
    this.setIsECommAvailable(this.options.eCommAvailable);
  },
  beforeUnmount() {
    window.removeEventListener('load', this.pdpFullyLoaded());
  },
  methods: {
    ...mapMutations('productDetails', [
      'setBundleDialogVisibility',
      'setIsECommAvailable',
      'setMechanicNotes',
      'setVariantCode',
    ]),
    ...mapActions('productDetails', ['updatePdpDetailsLoaded']),
    ...mapMutations('productBundle', ['setIsVariantsChanged']),

    onAvailabilityChange(status) {
      this.hasAvailability = status;
    },
    pdpFullyLoaded() {
      this.updatePdpDetailsLoaded(true);
    },
    beforeAddToCart(quantity) {
      this.quantity = quantity;
      this.addToCart(this.variantCode, this.quantity);
    },
  },
};
</script>
<style scoped lang="scss">
:deep(.bundle-dialog) {
  max-width: 1200px;

  .buttons {
    @include breakpoint(small down) {
      flex-direction: column;
    }
  }
}
</style>
